import React, { useState } from 'react';
import { SlidersHorizontal, Plus, Trash2, Check } from 'lucide-react';
import type { TaskCustomFieldDef } from '../../lib/types';

/**
 * Campi personalizzati della task (spunto Trello "Campi personalizzati", partner Unidevs).
 * Le definizioni valgono per il progetto (projects.metadata.task_custom_fields),
 * i valori per la singola task (tasks.custom_fields). Stile Notion: righe
 * "etichetta a sinistra, valore a destra", gestione campi in linea.
 */

type FieldValue = string | number | boolean | null;

interface TaskCustomFieldsProps {
  definitions: TaskCustomFieldDef[];
  values: Record<string, FieldValue>;
  canManage: boolean;
  onChangeValue: (fieldId: string, value: FieldValue) => void;
  onSaveDefinitions: (defs: TaskCustomFieldDef[]) => Promise<void> | void;
}

const TYPE_LABELS: Record<TaskCustomFieldDef['type'], string> = {
  text: 'Testo',
  number: 'Numero',
  date: 'Data',
  select: 'Scelta',
  checkbox: 'Spunta',
};

export function newFieldId(): string {
  return `cf_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function TaskCustomFields({ definitions, values, canManage, onChangeValue, onSaveDefinitions }: TaskCustomFieldsProps) {
  const [managing, setManaging] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftType, setDraftType] = useState<TaskCustomFieldDef['type']>('text');
  const [draftOptions, setDraftOptions] = useState('');
  const [draftOnCard, setDraftOnCard] = useState(true);
  const [saving, setSaving] = useState(false);

  if (definitions.length === 0 && !canManage) return null;

  const addField = async () => {
    const name = draftName.trim();
    if (!name) return;
    const def: TaskCustomFieldDef = {
      id: newFieldId(),
      name,
      type: draftType,
      show_on_card: draftOnCard,
      ...(draftType === 'select'
        ? { options: draftOptions.split(',').map(o => o.trim()).filter(Boolean) }
        : {}),
    };
    setSaving(true);
    try {
      await onSaveDefinitions([...definitions, def]);
      setDraftName('');
      setDraftOptions('');
    } finally {
      setSaving(false);
    }
  };

  const removeField = async (id: string) => {
    setSaving(true);
    try {
      await onSaveDefinitions(definitions.filter(d => d.id !== id));
    } finally {
      setSaving(false);
    }
  };

  const toggleOnCard = async (id: string) => {
    await onSaveDefinitions(definitions.map(d => (d.id === id ? { ...d, show_on_card: !d.show_on_card } : d)));
  };

  return (
    <div className="border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 bg-[#FBFBFA] dark:bg-gray-800">
        <div className="flex items-center space-x-2">
          <SlidersHorizontal className="h-4 w-4 text-[#787774] dark:text-gray-400" />
          <span className="text-sm font-medium text-[#37352F] dark:text-gray-100">Campi personalizzati</span>
          {definitions.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{definitions.length}</span>
          )}
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setManaging(v => !v)}
            className="text-xs text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200"
          >
            {managing ? 'Fine' : 'Gestisci campi'}
          </button>
        )}
      </div>

      <div className="p-3 pt-2 space-y-1">
        {definitions.length === 0 && !managing && (
          <p className="text-xs text-[#9B9A97] dark:text-gray-500">
            Nessun campo per questo progetto. Con "Gestisci campi" aggiungi ad esempio Budget, Cliente finale o Canale.
          </p>
        )}

        {definitions.map(def => (
          <div key={def.id} className="flex items-center gap-2 py-0.5">
            <span className="w-32 flex-shrink-0 text-xs text-[#787774] dark:text-gray-400 truncate" title={def.name}>{def.name}</span>
            {managing ? (
              <div className="flex-1 flex items-center justify-between gap-2">
                <span className="text-[11px] text-[#9B9A97]">{TYPE_LABELS[def.type]}{def.options?.length ? ` · ${def.options.join(', ')}` : ''}</span>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1 text-[11px] text-[#787774] cursor-pointer">
                    <input type="checkbox" checked={def.show_on_card !== false} onChange={() => void toggleOnCard(def.id)} className="rounded border-gray-300 text-[#2383E2] focus:ring-[#2383E2]" />
                    sulla card
                  </label>
                  <button type="button" onClick={() => void removeField(def.id)} disabled={saving} className="p-1 text-[#9B9A97] hover:text-[#EB5757]" title="Rimuovi campo">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ) : (
              <FieldInput def={def} value={values[def.id] ?? null} onChange={(v) => onChangeValue(def.id, v)} />
            )}
          </div>
        ))}

        {managing && (
          <div className="mt-2 pt-2 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-1.5">
              <input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void addField(); }}
                placeholder="Nome campo"
                className="flex-1 min-w-[120px] text-xs px-2 py-1 rounded border border-[rgba(55,53,47,0.16)] dark:border-gray-600 bg-white dark:bg-gray-900 text-[#37352F] dark:text-gray-100 outline-none focus:border-[#2383E2]"
              />
              <select
                value={draftType}
                onChange={(e) => setDraftType(e.target.value as TaskCustomFieldDef['type'])}
                className="text-xs px-2 py-1 rounded border border-[rgba(55,53,47,0.16)] dark:border-gray-600 bg-white dark:bg-gray-900 text-[#37352F] dark:text-gray-100"
              >
                {(Object.keys(TYPE_LABELS) as TaskCustomFieldDef['type'][]).map(t => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
              <label className="inline-flex items-center gap-1 text-[11px] text-[#787774] cursor-pointer">
                <input type="checkbox" checked={draftOnCard} onChange={(e) => setDraftOnCard(e.target.checked)} className="rounded border-gray-300 text-[#2383E2] focus:ring-[#2383E2]" />
                sulla card
              </label>
              <button
                type="button"
                onClick={() => void addField()}
                disabled={saving || !draftName.trim()}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-[#2383E2] text-white rounded hover:bg-[#1a6dc0] disabled:opacity-50"
              >
                <Plus className="h-3 w-3" />
                Aggiungi
              </button>
            </div>
            {draftType === 'select' && (
              <input
                value={draftOptions}
                onChange={(e) => setDraftOptions(e.target.value)}
                placeholder="Opzioni separate da virgola (es. Basso, Medio, Alto)"
                className="mt-1.5 w-full text-xs px-2 py-1 rounded border border-[rgba(55,53,47,0.16)] dark:border-gray-600 bg-white dark:bg-gray-900 text-[#37352F] dark:text-gray-100 outline-none focus:border-[#2383E2]"
              />
            )}
            <p className="mt-1.5 text-[11px] text-[#9B9A97]">I campi valgono per tutte le task di questo progetto.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldInput({ def, value, onChange }: { def: TaskCustomFieldDef; value: FieldValue; onChange: (v: FieldValue) => void }) {
  const inputClass =
    'flex-1 min-w-0 text-sm bg-[#F1F1EF] dark:bg-gray-800 rounded-md px-2 py-1 text-[#37352F] dark:text-gray-100 placeholder:text-[#9B9A97] outline-none focus:bg-[#EAEAEA] dark:focus:bg-gray-700 transition-colors';

  if (def.type === 'checkbox') {
    const checked = value === true;
    return (
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-[#35A76C] border-[#35A76C]' : 'border-[#C4C4C4] dark:border-gray-500'}`}
      >
        {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </button>
    );
  }
  if (def.type === 'select') {
    return (
      <select value={value === null ? '' : String(value)} onChange={(e) => onChange(e.target.value || null)} className={inputClass}>
        <option value="">—</option>
        {(def.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <DebouncedInput
      type={def.type === 'number' ? 'number' : def.type === 'date' ? 'date' : 'text'}
      value={value === null || value === undefined ? '' : String(value)}
      onCommit={(raw) => {
        if (raw === '') return onChange(null);
        onChange(def.type === 'number' ? Number(raw) : raw);
      }}
      className={inputClass}
      placeholder="Vuoto"
    />
  );
}

function DebouncedInput({ value, onCommit, ...rest }: { value: string; onCommit: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [draft, setDraft] = useState(value);
  const lastCommitted = React.useRef(value);
  React.useEffect(() => { setDraft(value); lastCommitted.current = value; }, [value]);
  const commit = () => {
    if (draft === lastCommitted.current) return;
    lastCommitted.current = draft;
    onCommit(draft);
  };
  return (
    <input
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') setDraft(value);
      }}
    />
  );
}

export function CustomFieldChips({ definitions, values }: { definitions: TaskCustomFieldDef[]; values: Record<string, FieldValue> }) {
  const chips = definitions
    .filter(d => d.show_on_card !== false)
    .map(d => {
      const v = values[d.id];
      if (v === null || v === undefined || v === '' || v === false) return null;
      let text: string;
      if (v === true) text = d.name;
      else if (d.type === 'date') text = `${d.name}: ${new Date(String(v)).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}`;
      else text = `${d.name}: ${String(v)}`;
      return { id: d.id, text };
    })
    .filter(Boolean) as Array<{ id: string; text: string }>;
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {chips.map(c => (
        <span key={c.id} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-black/[0.04] dark:bg-white/10 text-black/60 dark:text-white/70 max-w-full truncate" title={c.text}>
          {c.text}
        </span>
      ))}
    </div>
  );
}
